// src/_app.tsx
import { useState, useEffect, Suspense, lazy, useCallback } from "react";
import PrivateRoute from "components/Auth/PrivateRoute";
import ProtectedRoute from "components/common/ProtectedRoute";
import { useTranslation } from "react-i18next";
import {
  Switch,
  Link as RouterLink,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import {
  ListItemIcon,
  ListItemText,
  ListItem,
  ListItemButton,
  List,
  AppBar,
  Alert,
  Snackbar,
  Drawer,
  Hidden,
  IconButton,
  MenuItem,
  Typography,
  TextField,
  Box,
  Tooltip,
  Link,
  Button,
  Badge,
  Divider,
  Popover,
} from "@mui/material";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { hide, show } from "store/reducers/notifications.reducer";

// MaterialUI Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import GetAppIcon from "@mui/icons-material/GetApp";
import BuilderIcon from "assets/icons/BuilderIcon";
import DashboardIcon from "@mui/icons-material/DonutLarge";
import StrategiesIcon from "assets/icons/StrategiesIcon";
import SettingsIcon from "@mui/icons-material/Settings";
import FolderIcon from "@mui/icons-material/Folder";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import TimelineIcon from "@mui/icons-material/Timeline";
import HelpIcon from "@mui/icons-material/Help";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AdbIcon from "@mui/icons-material/Adb";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LogoutIcon from "@mui/icons-material/Logout";
// src/_app.tsx (zona de imports de MUI Icons)
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CloseIcon from "@mui/icons-material/Close";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CalendarMonthIcon from "@mui/icons-material/CalendarViewMonth";

// types
import type { TFunction } from "react-i18next";
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from "react";

// Assets
import Logo from "assets/img/logo_square_white.png";
import LogoTitle from "assets/img/text_logo_white.png";
import Spanish from "assets/icons/Spanish";
import British from "assets/icons/British";

// Components & Views
import useOrientation from "hooks/useOrientation";
import RotateView from "components/common/RotateView";
import Dashboard from "views/Dashboard";
import UserZone from "components/common/UserZone";
import ReferralPromoPopup from "components/common/ReferralPromoPopup";
import Login from "components/Auth/Login";
import BinanceIcon from "assets/icons/BinanceIcon";
import customAxios from "lib/axios";
import LoadingShow from "components/common/LoadingShow";
import UXWizzTracker from "components/Analytics/UXWizzTracker";

import OpenAccount from "components/TradingRobots/OpenAccount";
import CreateAccount from "components/TradingRobots/CreateAccount";
import LinkAccount from "components/TradingRobots/LinkAccount";
import LinkingProgress from "components/TradingRobots/LinkingProgress";
import ForgotPassword from "views/ForgotPassword";
import ResetPassword from "views/ResetPassword";
import AutoLogin from "views/AutoLogin";
import Courses from "views/Courses";
import Modules from "components/Courses/Modules";
import LessonPage from "components/Courses/LessonPage";
import {
  isPushNotificationsSupported,
  subscribeToPushNotifications,
  syncExistingPushSubscriptionSilently,
} from "lib/pushNotifications";
import { syncAppDeviceState } from "lib/appDeviceTracking";
import {
  fetchNotificationCenterItems,
  fetchUnreadNotificationsCount,
  dismissNotification,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  type NotificationCenterItem,
} from "lib/notificationCenter";

// Pages with lazy import
const TradingRobots = lazy(() => import("views/TradingRobots"));
const Robots = lazy(() => import("views/Robots"));
const MyValidation = lazy(() => import("views/MyValidation"));
const Binance = lazy(() => import("views/Binance"));
const Builder = lazy(() => import("views/BuilderWithDnd"));
const Profile = lazy(() => import("views/Profile"));
const Strategies = lazy(() => import("views/Strategies"));
const BalanceChart = lazy(() => import("views/BalanceChart"));
const Validation = lazy(() => import("views/Validation"));
const RobotValidation = lazy(() => import("views/RobotValidation"));
const TradingView = lazy(() => import("views/TradingView"));
const Download = lazy(() => import("views/Download"));
const DownloadRobot = lazy(() => import("views/DownloadRobot"));
// src/_app.tsx (zona de lazy imports)
const Tournament = lazy(() => import("views/Tournament"));
const Optimization = lazy(() => import("views/OptimizationWithDnd"));
const Optimized = lazy(() => import("views/Optimized"));
const OptimizationTrials = lazy(() => import("views/OptimizationTrials"));
const AdvancedReport = lazy(() => import("views/AdvancedReport"));
const BrokerUserAdmin = lazy(() => import("views/BrokerUserAdmin"));
const PushMetricsAdmin = lazy(() => import("views/PushMetricsAdmin"));
const EconomicCalendar = lazy(() => import("views/EconomicCalendar"));

const drawerWidth = 240;
const drawerSmallWidth = 70;

const links = [
  {
    id: 0,
    to: "/dashboard",
    active: true,
    name: "dashboard",
    label: "tabs.dashboard",
    icon: <DashboardIcon />,
  },
  {
    id: 8,
    to: "/tradingrobots/main",
    active: true,
    name: "tradingrobots",
    label: "tabs.tradingrobots",
    icon: <AdbIcon />,
  },
  {
    id: 1,
    to: "/builder",
    active: true,
    name: "builder",
    label: "tabs.builder",
    icon: <BuilderIcon />,
  },
  {
    id: 2,
    to: "/myvalidation",
    active: true,
    name: "myvalidation",
    label: "tabs.myvalidation",
    icon: <FolderIcon />,
  },
  {
    id: 3,
    to: "/strategies",
    active: true,
    name: "strategies",
    label: "tabs.strategies",
    icon: <StrategiesIcon />,
  },

  {
    id: 7,
    to: "/robots",
    active: true,
    name: "robots",
    label: "tabs.robots",
    icon: <SmartToyIcon />,
  },
  {
    id: 10,
    to: "/tournament",
    active: true,
    name: "tournament",
    label: "tabs.tournament",
    icon: <EmojiEventsIcon />,
  },

  {
    id: 4,
    to: "/tradingview",
    active: true,
    name: "tradingview",
    label: "tabs.tradingview",
    icon: <TimelineIcon />,
  },

  {
    id: 5,
    to: "/binance",
    active: false,
    name: "binance",
    label: "Binance",
    icon: <BinanceIcon />,
  },
  {
    id: 13,
    to: "/economic-calendar",
    active: true,
    name: "economic-calendar",
    label: "tabs.economic-calendar",
    icon: <CalendarMonthIcon />,
  },
  {
    id: 6,
    to: "/profile",
    active: true,
    name: "profile",
    label: "tabs.user-settings",
    icon: <SettingsIcon />,
  },
  {
    id: 9,
    to: "/myCourses",
    active: true,
    name: "courses",
    label: "tabs.courses",
    icon: <MenuBookIcon />,
  },
  {
    id: 11,
    to: "/broker-admin",
    active: true,
    name: "broker-admin",
    label: "tabs.broker-admin",
    icon: <AdminPanelSettingsIcon />,
    brokerAdminOnly: true,
  },
  {
    id: 12,
    to: "/push-metrics",
    active: true,
    name: "push-metrics",
    label: "tabs.push-metrics",
    icon: <QueryStatsIcon />,
    brokerAdminOnly: true,
  },
];

const languages = [
  { value: "es", label: "Español", icon: <Spanish /> },
  { value: "en", label: "English", icon: <British /> },
];

type DrawerContentProps = {
  t: TFunction<"global">;
  activePage: string;
  betaTester: boolean;
  brokerAdminEnabled: boolean;
  onNavigate?: () => void;
  includeAppControls?: boolean;
  currentLanguage?: string;
  onLanguageChange?: (language: string) => void;
  onLogout?: () => void;
};

type DeferredInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
};

const PWA_INSTALL_BANNER_DISMISS_KEY = "pwa_install_banner_hidden_until";
const PWA_INSTALL_BANNER_HIDE_FOR_MS = 7 * 24 * 60 * 60 * 1000;
const PWA_PUSH_BANNER_DISMISS_KEY = "pwa_push_banner_hidden_until";
const PWA_PUSH_BANNER_HIDE_FOR_MS = 7 * 24 * 60 * 60 * 1000;

const DrawerContent = function ({
  t,
  activePage,
  betaTester,
  brokerAdminEnabled,
  onNavigate,
  includeAppControls = false,
  currentLanguage,
  onLanguageChange,
  onLogout,
}: DrawerContentProps) {
  return (
    <List sx={{ border: 0 }}>
      {links
        .filter((link) => link.active)
        .filter((link) => (link.id === 7 ? betaTester : true))
        .filter((link) => (link.brokerAdminOnly ? brokerAdminEnabled : true))
        .map((link) => (
          <ListItemButton
            component={RouterLink}
            key={link.id}
            to={link.to}
            onClick={onNavigate}
            selected={activePage === link.to}
            sx={{
              borderRadius: 0,
              color: "#85858C",
              "& .MuiListItemIcon-root": {
                color: "#85858C",
                fill: "#85858C",
              },
              "&.MuiListItemButton-root.Mui-selected": {
                color: "primary.main",
                fill: "primary.main",
                backgroundColor: ["rgba(39, 170, 225, 0.2)", "!important"],
                borderRight: "3px solid #27AAE1",
                "& .MuiListItemIcon-root": {
                  color: "primary.main",
                  fill: "primary.main",
                },
              },
            }}
          >
            <ListItemIcon>{link.icon}</ListItemIcon>
            <ListItemText primary={t(link.label)} />
          </ListItemButton>
        ))}
      <ListItemButton
        component="a"
        href="https://web.tradeasy.tech/blog/"
        target="_blank"
        onClick={onNavigate}
        sx={{ color: "#85858C" }}
      >
        <ListItemIcon sx={{ color: "#85858C" }}>
          <NewspaperIcon />
        </ListItemIcon>
        <ListItemText primary={t("tabs.news")} />
      </ListItemButton>
      <ListItemButton
        component="a"
        href="https://help.tradeasy.tech/"
        target="_blank"
        onClick={onNavigate}
        sx={{ color: "#85858C" }}
      >
        <ListItemIcon sx={{ color: "#85858C" }}>
          <HelpIcon />
        </ListItemIcon>
        <ListItemText primary={t("tabs.help")} />
      </ListItemButton>
      {includeAppControls && onLanguageChange && onLogout && (
        <>
          <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.12)" }} />
          {languages.map((language) => (
            <ListItemButton
              key={language.value}
              onClick={() => {
                onLanguageChange(language.value);
                onNavigate?.();
              }}
              selected={currentLanguage === language.value}
              sx={{
                color: "#85858C",
                "& .MuiListItemIcon-root": {
                  color: "#85858C",
                },
                "&.MuiListItemButton-root.Mui-selected": {
                  color: "primary.main",
                  backgroundColor: ["rgba(39, 170, 225, 0.2)", "!important"],
                  "& .MuiListItemIcon-root": {
                    color: "primary.main",
                  },
                },
              }}
            >
              <ListItemIcon>{language.icon}</ListItemIcon>
              <ListItemText
                primary={
                  language.value === "es"
                    ? t("languageSpanish")
                    : t("languageEnglish")
                }
                secondary={t("language")}
                secondaryTypographyProps={{
                  sx: { color: "inherit", opacity: 0.75 },
                }}
              />
            </ListItemButton>
          ))}
          <ListItemButton
            onClick={() => {
              onNavigate?.();
              onLogout();
            }}
            sx={{ color: "#85858C" }}
          >
            <ListItemIcon sx={{ color: "#85858C" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t("logout")} />
          </ListItemButton>
        </>
      )}
    </List>
  );
};

const isAuthenticated = () => !!localStorage.getItem("user_id");

const Router = () => {
  const { isDesktop, isPortrait } = useOrientation();
  return (
    <Suspense fallback={<LoadingShow />}>
      <Switch>
        <Route exact path={["/dashboard", "/"]} component={Dashboard} />
        <PrivateRoute path="/tradingrobots/main" authed={isAuthenticated()}>
          <TradingRobots />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/OpenAccount"
          authed={isAuthenticated()}
        >
          <OpenAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/CreateAccount"
          authed={isAuthenticated()}
        >
          <CreateAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/LinkAccount"
          authed={isAuthenticated()}
        >
          <LinkAccount />
        </PrivateRoute>
        <PrivateRoute
          path="/tradingrobots/LinkingProgress"
          authed={isAuthenticated()}
        >
          <LinkingProgress />
        </PrivateRoute>

        <Route
          path="/builder"
          component={
            isPortrait && !isDesktop
              ? () => <RotateView title="titles.builder" />
              : Builder
          }
        />
        <Route path="/myvalidation" component={MyValidation} />
        <Route path="/strategies" component={Strategies} />
        <Route path="/tradingview" component={TradingView} />
        <Route path="/binance" component={Binance} />
        <Route path="/broker-admin" component={BrokerUserAdmin} />
        <Route path="/push-metrics" component={PushMetricsAdmin} />
        <Route path="/profile" component={Profile} />
        <Route path="/download" component={Download} />
        <Route path="/downloadrobot" component={DownloadRobot} />
        <Route path="/robots" component={Robots} />

        <Route exact path="/economic-calendar" component={EconomicCalendar} />
        <Route
          path="/validation"
          component={
            isPortrait && !isDesktop
              ? () => <RotateView title="titles.validation" />
              : Validation
          }
        />
        <Route
          exact
          path={["/generate_balance_chart", "/"]}
          component={BalanceChart}
        />
        <Route path="/RobotValidation" component={RobotValidation} />
        <Route
          path="/validation-report/:id"
          render={() => <AdvancedReport mode="validation" />}
        />
        <Route
          path="/robot-validation-report/:id"
          render={() => <AdvancedReport mode="robotvalidation" />}
        />

        {/* Rutas públicas */}
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/autologin" component={AutoLogin} />

        {/* Ruta torneo */}
        <Route path="/tournament" component={Tournament} />

        {/* Ruta optimization */}
        <Route path="/optimization" component={Optimization} />

        <Route path="/optimized" component={Optimized} />

        <Route path="/optimization-trials" component={OptimizationTrials} />

        {/* Cursos */}
        <Route exact path="/myCourses" component={Courses} />
        <ProtectedRoute exact path="/myCourses/:courseId" component={Modules} />
        <ProtectedRoute
          exact
          path="/myCourses/:courseId/modules/:moduleId/lesson/:lessonId/page/:pageNumber"
          component={LessonPage}
        />
        <Route
          exact
          path="/myCourses/:courseId/modules/:moduleId/lesson/:lessonId"
          render={({ match }) => {
            const { courseId, moduleId, lessonId } = match.params;
            return (
              <Redirect
                to={`/myCourses/${courseId}/modules/${moduleId}/lesson/${lessonId}/page/1`}
              />
            );
          }}
        />
      </Switch>
    </Suspense>
  );
};

const App = () => {
  const [t, i18n] = useTranslation("global");
  const [isUserFree, setIsUserFree] = useState(
    () => localStorage.getItem("type_user") === "F"
  );
  const [u] = useTranslation("freemium");
  const { isDesktop } = useOrientation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState("dashboard");
  const notifications = useAppSelector((state) => state.notifications);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const UrlPath = window.location.pathname.split("/")[1];
  const isDashboard = !!(UrlPath === "dashboard" || UrlPath === "");
  const history = useHistory();
  const [brokerAdminEnabled, setBrokerAdminEnabled] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] =
    useState<DeferredInstallPromptEvent | null>(null);
  const [canInstallApp, setCanInstallApp] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIosInstallHint, setShowIosInstallHint] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<
    NotificationPermission | "unsupported" | "missing-config"
  >("default");
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [pushSubscribeLoading, setPushSubscribeLoading] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<HTMLElement | null>(null);
  const [notificationItems, setNotificationItems] = useState<
    NotificationCenterItem[]
  >([]);
  const [notificationCenterLoading, setNotificationCenterLoading] =
    useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const from = (location.state as any)?.from as string | undefined;

  // origen guardado (Top10/Trials)
  const optimizationsFromStored =
    sessionStorage.getItem("optimizations_from") || undefined;

  // SOLO queremos flecha si vienes del flujo Top10/Trials
  const showBackFromOptimizations =
    (typeof from === "string" &&
      (from.startsWith("/optimized") ||
        from.startsWith("/optimization-trials"))) ||
    (UrlPath === "builder" &&
      typeof optimizationsFromStored === "string" &&
      (optimizationsFromStored.startsWith("/optimized") ||
        optimizationsFromStored.startsWith("/optimization-trials")));

  const trialsFromStored = sessionStorage.getItem("trials_from") || undefined;

  useEffect(() => {
    // si llegamos a Optimized desde Trials, guardamos esa URL
    if (
      UrlPath === "optimized" &&
      typeof from === "string" &&
      from.startsWith("/optimization-trials")
    ) {
      sessionStorage.setItem("trials_from", from);
    }

    // opcional: si sales del flujo de optimizaciones, limpias
    if (
      UrlPath !== "optimized" &&
      UrlPath !== "builder" &&
      UrlPath !== "validation"
    ) {
      sessionStorage.removeItem("trials_from");
    }
  }, [UrlPath, from]);

  const goBackSmart = () => {
    if (!showBackFromOptimizations) return;

    if (UrlPath === "builder" && optimizationsFromStored) {
      // 👇 marca que vamos a crear un “duplicado” de optimized en el history
      sessionStorage.setItem("optimized_skip_once", "1");

      history.replace(optimizationsFromStored);
      sessionStorage.removeItem("optimizations_from");
      return;
    }

    history.goBack();
  };

  const goBackOptimized = () => {
    const skip = sessionStorage.getItem("optimized_skip_once") === "1";

    if (skip) {
      sessionStorage.removeItem("optimized_skip_once");
      history.go(-2); // ✅ salta el optimized duplicado y cae en trials
      return;
    }

    history.goBack();
  };

  const goBack = () => {
    // si hay historial, vuelve atrás
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    // fallback si entraste directo por URL
    // (aquí puedes decidir un destino fijo)
    // por ejemplo: volver a strategies
    window.location.href = "/strategies";
  };

  const goStrategies = () => {
    history.replace("/strategies");
  };

  // --- AUTENTICACIÓN + RUTAS PÚBLICAS
  const authed = !!localStorage.getItem("user_id");
  const isPublicRoute =
    location.pathname.includes("forgot-password") ||
    location.pathname.includes("reset-password") ||
    location.pathname.includes("autologin");

  // Mostrar modal de login solo cuando NO hay sesión y NO es ruta pública
  const showLoginModal = !authed && !isPublicRoute;

  /* *********************************** // Check if the user is a beta tester //   ******************************************** */
  const [betaTester, setBetaTester] = useState(false);
  const findBetaTester = async () => {
    try {
      const beta = true;
      setBetaTester(beta);
    } catch (error) {
      console.error("Error fetching user data:", error);
      return false;
    }
  };

  useEffect(() => {
    // Si llegamos a BUILDER desde optimized / optimization-trials, guardamos ese "from"
    if (
      UrlPath === "builder" &&
      typeof from === "string" &&
      (from.startsWith("/optimized") || from.startsWith("/optimization-trials"))
    ) {
      sessionStorage.setItem("optimizations_from", from);
    }

    // Si salimos del flujo (ni builder ni validation), limpiamos
    if (UrlPath !== "builder" && UrlPath !== "validation") {
      sessionStorage.removeItem("optimizations_from");
    }
  }, [UrlPath, from]);

  useEffect(() => {
    findBetaTester();
    let actualLocation = location.pathname;
    if (actualLocation === "/") {
      actualLocation = "/dashboard";
    }
    setPage(actualLocation);
  }, [location, location.pathname]);

  const changeLanguage = useCallback(
    (language: string) => {
      i18n.changeLanguage(language);
    },
    [i18n]
  );

  const handleLanguageChange = (event: ChangeEvent<HTMLInputElement>) => {
    changeLanguage(event.target.value);
  };
  const handleSnackbarClose = () => dispatch(hide());
  const handleDrawerToggle = () => setMobileOpen((s) => !s);
  const handleMobileDrawerClose = () => setMobileOpen(false);
  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_email");
      localStorage.removeItem("type_user");
      localStorage.removeItem("user_valid_count");
    } catch {
      console.log("error al remover user");
    }
    window.location.reload();
  }, []);
  const dismissInstallBanner = useCallback((persist = true) => {
    if (persist) {
      localStorage.setItem(
        PWA_INSTALL_BANNER_DISMISS_KEY,
        String(Date.now() + PWA_INSTALL_BANNER_HIDE_FOR_MS)
      );
    }
    setShowInstallBanner(false);
  }, []);
  const dismissPushBanner = useCallback((persist = true) => {
    if (persist) {
      localStorage.setItem(
        PWA_PUSH_BANNER_DISMISS_KEY,
        String(Date.now() + PWA_PUSH_BANNER_HIDE_FOR_MS)
      );
    }
    setShowPushBanner(false);
  }, []);

  const refreshUserShellState = useCallback(
    async (signal?: AbortSignal) => {
      if (!authed) return;

      try {
        const { data } = await customAxios(`/user/validationCount`, {
          signal: signal as any,
        });

        if (
          Number(localStorage.getItem("user_valid_count")) !==
          data.validation_count
        ) {
          localStorage.setItem(
            "user_valid_count",
            String(data.validation_count)
          );
        }

        if (data?.type_user) {
          localStorage.setItem("type_user", data.type_user);
          setIsUserFree(data.type_user === "F");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
      }

      try {
        const { data } = await customAxios.get(
          "/tradingrobots/admin-brokers/can-access",
          { signal: signal as any }
        );
        setBrokerAdminEnabled(Boolean(data?.canAccess));
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        setBrokerAdminEnabled(false);
      }
    },
    [authed]
  );

  useEffect(() => {
    let actualLocation = location.pathname;
    if (actualLocation === "/") {
      actualLocation = "/dashboard";
    }
    setPage(actualLocation);
    return () => {};
  }, [location, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // --- EFECTO QUE HACÍA LLAMADAS AL BACK: ahora solo si authed
  useEffect(() => {
    if (!authed) return; // ⛔️ No llames al back antes de login

    const controller = new AbortController();

    async function fetchApi() {
      try {
        const { data } = await customAxios(`/user/validationCount`, {
          signal: controller.signal as any,
        });

        if (
          Number(localStorage.getItem("user_valid_count")) !==
          data.validation_count
        ) {
          localStorage.setItem(
            "user_valid_count",
            String(data.validation_count)
          );
        }
        if (data?.type_user) {
          localStorage.setItem("type_user", data.type_user);
          setIsUserFree(data.type_user === "F");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          // petición cancelada al desmontar
          return;
        }
        localStorage.setItem("user_valid_count", "0");
      }
    }

    fetchApi();
    return () => controller.abort();
  }, [authed]);

  useEffect(() => {
    if (!authed) {
      setBrokerAdminEnabled(false);
      return;
    }

    let mounted = true;
    customAxios
      .get("/tradingrobots/admin-brokers/can-access")
      .then(({ data }) => {
        if (!mounted) return;
        setBrokerAdminEnabled(Boolean(data?.canAccess));
      })
      .catch(() => {
        if (!mounted) return;
        setBrokerAdminEnabled(false);
      });

    return () => {
      mounted = false;
    };
  }, [authed]);

  useEffect(() => {
    if (!authed) return;

    let currentController: AbortController | null = null;
    let refreshInFlight = false;

    const runRefresh = () => {
      if (document.visibilityState === "hidden" || refreshInFlight) return;

      currentController?.abort();
      currentController = new AbortController();
      refreshInFlight = true;

      refreshUserShellState(currentController.signal).finally(() => {
        refreshInFlight = false;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runRefresh();
      }
    };

    const handleFocus = () => {
      runRefresh();
    };

    const handleOnline = () => {
      runRefresh();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      currentController?.abort();
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authed, refreshUserShellState]);

  useEffect(() => {
    const updateInstallState = () => {
      const nav = window.navigator as Navigator & { standalone?: boolean };
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        nav.standalone === true;
      const ua = window.navigator.userAgent.toLowerCase();
      const isIosDevice = /iphone|ipad|ipod/.test(ua);
      const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);

      setIsStandalone(standalone);
      setShowIosInstallHint(
        Boolean(!isDesktop && isIosDevice && isSafari && !standalone)
      );
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();

      if (isDesktop) {
        setDeferredInstallPrompt(null);
        setCanInstallApp(false);
        setShowInstallBanner(false);
        return;
      }

      setDeferredInstallPrompt(event as DeferredInstallPromptEvent);
      setCanInstallApp(true);
    };

    const handleAppInstalled = () => {
      localStorage.removeItem(PWA_INSTALL_BANNER_DISMISS_KEY);
      setDeferredInstallPrompt(null);
      setCanInstallApp(false);
      setShowIosInstallHint(false);
      setShowInstallBanner(false);
      updateInstallState();
    };

    updateInstallState();
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [isDesktop]);

  const updatePushState = useCallback(() => {
    const supported = isPushNotificationsSupported();
    setPushSupported(supported);

    if (
      !supported ||
      typeof window === "undefined" ||
      !("Notification" in window)
    ) {
      setPushPermission(supported ? "default" : "unsupported");
      return;
    }

    setPushPermission(Notification.permission);
  }, []);

  useEffect(() => {
    updatePushState();
  }, [updatePushState, isStandalone]);

  const syncCurrentAppDeviceState = useCallback(async () => {
    if (!authed || isPublicRoute) return;

    try {
      await syncAppDeviceState(location.pathname + location.search);
    } catch (error) {
      console.error("[pwa] Failed to sync app device state", error);
    }
  }, [authed, isPublicRoute, location.pathname, location.search]);

  useEffect(() => {
    syncCurrentAppDeviceState();
  }, [syncCurrentAppDeviceState, pushPermission, pushSupported, isStandalone]);

  useEffect(() => {
    if (!authed || isPublicRoute) return;

    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        syncCurrentAppDeviceState();
      }
    };
    const handleOnline = () => {
      syncCurrentAppDeviceState();
    };

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("online", handleOnline);
    };
  }, [authed, isPublicRoute, syncCurrentAppDeviceState]);

  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;

      setDeferredInstallPrompt(null);
      setCanInstallApp(false);

      if (choice.outcome === "accepted") {
        localStorage.removeItem(PWA_INSTALL_BANNER_DISMISS_KEY);
        setShowInstallBanner(false);
        dispatch(show({ type: "success", msg: t("pwa.installed") }));
      } else {
        dismissInstallBanner();
        dispatch(show({ type: "info", msg: t("pwa.installDeferred") }));
      }
      return;
    }

    if (showIosInstallHint) {
      dismissInstallBanner();
      dispatch(show({ type: "info", msg: t("pwa.installIosHint") }));
    }
  };

  const shouldShowInstallAppButton =
    authed &&
    !isDesktop &&
    !isStandalone &&
    (canInstallApp || showIosInstallHint);
  const shouldOfferInstallBanner =
    authed &&
    !isPublicRoute &&
    !isDesktop &&
    !isStandalone &&
    (canInstallApp || showIosInstallHint);

  useEffect(() => {
    if (!shouldOfferInstallBanner) {
      setShowInstallBanner(false);
      return;
    }

    const hiddenUntil = Number(
      localStorage.getItem(PWA_INSTALL_BANNER_DISMISS_KEY) || "0"
    );
    setShowInstallBanner(Date.now() >= hiddenUntil);
  }, [shouldOfferInstallBanner]);

  const shouldOfferPushBanner =
    authed &&
    !isPublicRoute &&
    !isDesktop &&
    isStandalone &&
    pushSupported &&
    pushPermission === "default";

  useEffect(() => {
    if (!shouldOfferPushBanner) {
      setShowPushBanner(false);
      return;
    }

    const hiddenUntil = Number(
      localStorage.getItem(PWA_PUSH_BANNER_DISMISS_KEY) || "0"
    );
    setShowPushBanner(Date.now() >= hiddenUntil);
  }, [shouldOfferPushBanner]);

  useEffect(() => {
    if (
      !authed ||
      !isStandalone ||
      !pushSupported ||
      pushPermission !== "granted"
    ) {
      return;
    }

    syncExistingPushSubscriptionSilently();
  }, [authed, isStandalone, pushSupported, pushPermission]);

  const refreshUnreadNotificationCount = useCallback(async () => {
    if (!authed || isPublicRoute) return;

    try {
      const unreadCount = await fetchUnreadNotificationsCount();
      setUnreadNotificationCount(unreadCount);
    } catch (error) {
      console.error("[notifications] Failed to refresh unread count", error);
    }
  }, [authed, isPublicRoute]);

  const loadNotificationCenter = useCallback(async () => {
    if (!authed || isPublicRoute) return;

    setNotificationCenterLoading(true);

    try {
      const [items, unreadCount] = await Promise.all([
        fetchNotificationCenterItems(20),
        fetchUnreadNotificationsCount(),
      ]);
      setNotificationItems(items);
      setUnreadNotificationCount(unreadCount);
    } catch (error) {
      console.error(
        "[notifications] Failed to load notification center",
        error
      );
      dispatch(
        show({ type: "error", msg: t("notificationsCenter.loadError") })
      );
    } finally {
      setNotificationCenterLoading(false);
    }
  }, [authed, isPublicRoute, dispatch, t]);

  useEffect(() => {
    refreshUnreadNotificationCount();
  }, [refreshUnreadNotificationCount]);

  useEffect(() => {
    if (!authed || isPublicRoute) return;

    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        refreshUnreadNotificationCount();
      }
    };
    const handleOnlineNotifications = () => {
      refreshUnreadNotificationCount();
    };
    const intervalId = window.setInterval(() => {
      refreshUnreadNotificationCount();
    }, 60000);

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("online", handleOnlineNotifications);

    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("online", handleOnlineNotifications);
      window.clearInterval(intervalId);
    };
  }, [authed, isPublicRoute, refreshUnreadNotificationCount]);

  const handleOpenNotificationCenter = async (
    event: ReactMouseEvent<HTMLElement>
  ) => {
    setNotificationsAnchorEl(event.currentTarget);
    await loadNotificationCenter();
  };

  const handleCloseNotificationCenter = () => {
    setNotificationsAnchorEl(null);
  };

  const handleMarkAllNotificationsAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotificationItems((items) =>
        items.map((item) => ({
          ...item,
          isRead: true,
          readAt: item.readAt || new Date().toISOString(),
        }))
      );
      setUnreadNotificationCount(0);
    } catch (error) {
      console.error("[notifications] Failed to mark all as read", error);
      dispatch(
        show({ type: "error", msg: t("notificationsCenter.markReadError") })
      );
    }
  };

  const handleNotificationItemClick = async (item: NotificationCenterItem) => {
    try {
      if (!item.isRead) {
        await markNotificationAsRead(item.id);
        setNotificationItems((items) =>
          items.map((current) =>
            current.id === item.id
              ? {
                  ...current,
                  isRead: true,
                  readAt: current.readAt || new Date().toISOString(),
                }
              : current
          )
        );
        setUnreadNotificationCount((count) => Math.max(0, count - 1));
      }
    } catch (error) {
      console.error(
        "[notifications] Failed to mark notification as read",
        error
      );
      dispatch(
        show({ type: "error", msg: t("notificationsCenter.markReadError") })
      );
    }

    handleCloseNotificationCenter();

    if (item.targetUrl) {
      history.push(item.targetUrl);
    }
  };

  const handleDismissNotification = async (item: NotificationCenterItem) => {
    try {
      await dismissNotification(item.id);
      setNotificationItems((items) =>
        items.filter((current) => current.id !== item.id)
      );
      if (!item.isRead) {
        setUnreadNotificationCount((count) => Math.max(0, count - 1));
      }
    } catch (error) {
      console.error("[notifications] Failed to dismiss notification", error);
      dispatch(
        show({ type: "error", msg: t("notificationsCenter.dismissError") })
      );
    }
  };

  const notificationsPopoverOpen = Boolean(notificationsAnchorEl);

  const formatNotificationDate = (value: string | null) => {
    if (!value) return t("notificationsCenter.justNow");

    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    } catch (_error) {
      return value;
    }
  };

  const handleEnablePushNotifications = async () => {
    setPushSubscribeLoading(true);

    try {
      const result = await subscribeToPushNotifications();
      setPushPermission(result.permission);

      if (result.ok) {
        localStorage.removeItem(PWA_PUSH_BANNER_DISMISS_KEY);
        setShowPushBanner(false);
        dispatch(show({ type: "success", msg: t("pwa.notificationsEnabled") }));
        return;
      }

      if (result.reason === "denied") {
        dismissPushBanner(false);
        dispatch(show({ type: "warning", msg: t("pwa.notificationsDenied") }));
        return;
      }

      if (result.reason === "dismissed") {
        dismissPushBanner();
        dispatch(show({ type: "info", msg: t("pwa.notificationsDeferred") }));
        return;
      }

      dispatch(show({ type: "error", msg: t("pwa.notificationsError") }));
    } catch (error) {
      console.error("[push] subscribe error", error);
      dispatch(show({ type: "error", msg: t("pwa.notificationsError") }));
    } finally {
      setPushSubscribeLoading(false);
      updatePushState();
    }
  };

  return (
    <>
      <UXWizzTracker />

      {showLoginModal && <Login />}

      {/* Monta el layout + router solo si hay sesión o estás en una ruta pública */}
      {(authed || isPublicRoute) && (
        <Box display="flex" width="100%" height="100%" bgcolor="#f7f7fa">
          {authed && <ReferralPromoPopup />}

          {/* Drawer lateral */}
          {authed && (
            <Box
              component="nav"
              sx={{
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                width: { md: isDashboard ? drawerWidth : drawerSmallWidth },
                flexShrink: { md: 0 },
                "&:hover": { width: drawerWidth },
              }}
            >
              <Hidden mdUp implementation="js">
                <Drawer
                  variant="temporary"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  sx={{
                    "& .MuiDrawer-paper": {
                      border: 0,
                      width: drawerWidth,
                      backgroundColor: "#1A2B47",
                      color: "#CECED4",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    },
                  }}
                  ModalProps={{ keepMounted: true }}
                >
                  <Box width="100%" p={4}>
                    <img src={LogoTitle} width="100%" alt="tradEAsy Logo" />
                  </Box>
                  <DrawerContent
                    t={t}
                    activePage={page}
                    betaTester={betaTester}
                    brokerAdminEnabled={brokerAdminEnabled}
                    onNavigate={handleMobileDrawerClose}
                    includeAppControls
                    currentLanguage={i18n.language}
                    onLanguageChange={changeLanguage}
                    onLogout={handleLogout}
                  />
                </Drawer>
              </Hidden>

              <Hidden mdDown implementation="js">
                <Drawer
                  sx={{
                    "& .MuiDrawer-paper": {
                      transition: (theme) =>
                        theme.transitions.create("width", {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.leavingScreen,
                        }),
                      overflowX: "hidden",
                      border: 0,
                      width: isDashboard ? drawerWidth : drawerSmallWidth,
                      backgroundColor: "#1A2B47",
                      color: "#CECED4",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      "&:hover": { width: drawerWidth },
                    },
                  }}
                  variant="permanent"
                  open
                >
                  <Box width={drawerWidth} py={4}>
                    <ListItem
                      component="a"
                      target="_blank"
                      href={process.env.REACT_APP_TRADEASY_WEBPAGE}
                    >
                      <ListItemIcon>
                        <img src={Logo} width="44px" alt="tradEAsy Logo" />
                      </ListItemIcon>
                      <ListItemText>
                        <img
                          src={LogoTitle}
                          width="150px"
                          alt="tradEAsy Logo"
                        />
                      </ListItemText>
                    </ListItem>
                  </Box>
                  <DrawerContent
                    t={t}
                    activePage={page}
                    betaTester={betaTester}
                    brokerAdminEnabled={brokerAdminEnabled}
                  />
                </Drawer>
              </Hidden>
            </Box>
          )}

          {/* Contenido principal */}
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            overflow="auto"
          >
            {authed && (
              <AppBar
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  py: 0.5,
                  px: { xs: 1, sm: 2 },
                }}
                position="sticky"
                color="transparent"
                elevation={0}
              >
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ display: { md: "none" } }}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>

                {(UrlPath === "builder" ||
                  (UrlPath === "validation" && typeof from !== "string")) &&
                  showBackFromOptimizations && (
                    <IconButton
                      onClick={goBackSmart}
                      size="medium"
                      sx={{ mr: 1 }}
                      color="primary"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}

                {UrlPath === "validation" && typeof from === "string" && (
                  <IconButton
                    onClick={() => {
                      // ✅ si hay historial real, vuelve sin duplicar entradas
                      if (window.history.length > 1) {
                        history.goBack();
                        return;
                      }
                      // fallback si entraste por URL directa
                      history.replace(from);
                    }}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "download" && (
                  <IconButton
                    onClick={() => window.history.back()}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                {UrlPath === "downloadrobot" && (
                  <IconButton
                    onClick={() => window.history.back()}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "optimized" && (
                  <IconButton
                    onClick={goBackOptimized}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                {UrlPath === "optimization-trials" && (
                  <IconButton
                    onClick={goStrategies}
                    size="medium"
                    sx={{ mr: 1 }}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}

                <Typography
                  variant="h5"
                  noWrap
                  sx={{ display: { xs: "none", md: "block" } }}
                  fontFamily="Catamaran"
                  fontWeight={500}
                >
                  {UrlPath ? t(`titles.${UrlPath}`) : t("titles.dashboard")}
                </Typography>

                {isUserFree && (
                  <Box
                    alignItems="center"
                    justifyContent="center"
                    marginLeft="auto"
                    sx={{ display: { xs: "none", md: "flex" } }}
                  >
                    <Typography>{u("former")}</Typography>&nbsp;
                    <Link href={u("plans_link")} target="_blank" component="a">
                      {u("improve")}
                    </Link>
                  </Box>
                )}

                <Box
                  marginLeft="auto"
                  display="flex"
                  my={1}
                  alignItems="center"
                  gap={0.5}
                  minWidth={0}
                >
                  {shouldShowInstallAppButton && (
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<GetAppIcon />}
                      onClick={handleInstallApp}
                      sx={{
                        mr: 1,
                        minWidth: "auto",
                        px: { xs: 1.25, sm: 1.5 },
                        whiteSpace: "nowrap",
                        borderStyle: "dashed",
                        bgcolor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      {t("pwa.installApp")}
                    </Button>
                  )}

                  <Tooltip
                    title={
                      <Typography variant="body2">
                        {t("notificationsCenter.title")}
                      </Typography>
                    }
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={handleOpenNotificationCenter}
                      sx={{ flexShrink: 0 }}
                    >
                      <Badge
                        color="error"
                        badgeContent={unreadNotificationCount}
                        invisible={unreadNotificationCount <= 0}
                      >
                        <NotificationsNoneIcon htmlColor="#CECED4" />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={<Typography variant="body2">{t("help")}</Typography>}
                    arrow
                  >
                    <IconButton
                      size="small"
                      href={process.env.REACT_APP_TRADEASY_HELP}
                      target="_blank"
                      component="a"
                      sx={{ display: { xs: "none", md: "inline-flex" } }}
                    >
                      <HelpIcon htmlColor="#CECED4" />
                    </IconButton>
                  </Tooltip>

                  <Box
                    display="flex"
                    alignItems="center"
                    bgcolor="white"
                    borderRadius="5px"
                    mx={0.5}
                    p={0.5}
                    sx={{ display: { xs: "none", md: "flex" } }}
                  >
                    <TextField
                      id="language-selector"
                      select
                      onChange={handleLanguageChange}
                      value={i18n.language}
                      sx={{
                        p: 0,
                        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                        "& .MuiOutlinedInput-input": { p: 0, display: "flex" },
                      }}
                    >
                      {languages.map((language) => (
                        <MenuItem
                          value={language.value}
                          key={language.value}
                          sx={{
                            display: "flex",
                            "& .MuiListItemIcon-root": { minWidth: 0 },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0 }}>
                            {language.icon}
                          </ListItemIcon>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <UserZone
                      helpHref={process.env.REACT_APP_TRADEASY_HELP}
                      currentLanguage={i18n.language}
                      onLanguageChange={changeLanguage}
                      languages={languages}
                    />
                  </Box>
                </Box>

                {isUserFree && (
                  <Box
                    width="100%"
                    sx={{ display: { xs: "block", md: "none" } }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      marginLeft="auto"
                      textAlign="center"
                    >
                      <Typography>
                        {u("former")}&nbsp;
                        <Link
                          href={u("plans_link")}
                          target="_blank"
                          component="a"
                        >
                          {u("improve")}
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </AppBar>
            )}

            <Box
              component="main"
              height="100%"
              width="100%"
              display="flex"
              flexDirection="column"
              overflow="auto"
            >
              {/* El Router se renderiza tanto en rutas públicas como con sesión */}
              <Router />
            </Box>
          </Box>
        </Box>
      )}

      <Popover
        open={notificationsPopoverOpen}
        anchorEl={notificationsAnchorEl}
        onClose={handleCloseNotificationCenter}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: "calc(100vw - 24px)", sm: 380 },
            maxWidth: 380,
            maxHeight: 520,
            mt: 1,
            borderRadius: 2,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
        >
          <Typography fontWeight={700}>
            {t("notificationsCenter.title")}
          </Typography>
          <Button
            size="small"
            onClick={handleMarkAllNotificationsAsRead}
            disabled={unreadNotificationCount <= 0}
          >
            {t("notificationsCenter.markAllRead")}
          </Button>
        </Box>
        <Divider />
        <Box>
          {notificationCenterLoading ? (
            <Box px={2} py={2}>
              <Typography variant="body2" color="text.secondary">
                {t("notificationsCenter.loading")}
              </Typography>
            </Box>
          ) : notificationItems.length === 0 ? (
            <Box px={2} py={2}>
              <Typography variant="body2" color="text.secondary">
                {t("notificationsCenter.empty")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notificationItems.map((item, index) => (
                <Box key={item.id}>
                  <Box
                    sx={{
                      bgcolor: item.isRead
                        ? "transparent"
                        : "rgba(39, 170, 225, 0.08)",
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <ListItemButton
                      alignItems="flex-start"
                      onClick={() => handleNotificationItemClick(item)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Box width="100%">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={item.isRead ? 600 : 700}
                            color="text.primary"
                          >
                            {item.title ||
                              t("notificationsCenter.defaultTitle")}
                          </Typography>
                          {!item.isRead && (
                            <Box
                              width={8}
                              height={8}
                              borderRadius="50%"
                              bgcolor="error.main"
                              flexShrink={0}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, whiteSpace: "normal" }}
                        >
                          {item.body || t("notificationsCenter.emptyBody")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1 }}
                        >
                          {formatNotificationDate(
                            item.deliveredAt || item.sentAt || item.createdAt
                          )}
                        </Typography>
                      </Box>
                    </ListItemButton>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      pr={1}
                      pt={1}
                      flexShrink={0}
                    >
                      <Tooltip
                        title={
                          <Typography variant="body2">
                            {t("notificationsCenter.hide")}
                          </Typography>
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleDismissNotification(item)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {index < notificationItems.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      <Snackbar
        onClose={handleSnackbarClose}
        autoHideDuration={5000}
        open={notifications.show}
      >
        <Alert
          severity={notifications.type}
          variant="filled"
          onClose={handleSnackbarClose}
        >
          {notifications.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showInstallBanner}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          display: { xs: "flex", md: "none" },
          bottom: { xs: 16, sm: 24 },
          px: 2,
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 520,
            alignItems: "flex-start",
            boxShadow: 6,
            "& .MuiAlert-message": { width: "100%" },
          }}
          action={
            <Box display="flex" gap={1} alignItems="center" mt={0.5}>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => dismissInstallBanner()}
                sx={{
                  borderColor: "rgba(255,255,255,0.45)",
                  color: "inherit",
                }}
              >
                {t("pwa.later")}
              </Button>
              <Button
                color="inherit"
                size="small"
                variant="contained"
                onClick={handleInstallApp}
                sx={{
                  backgroundColor: "#fff",
                  color: "info.main",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                }}
              >
                {t("pwa.installApp")}
              </Button>
            </Box>
          }
        >
          <Box>
            <Typography component="div" fontWeight={700}>
              {t("pwa.installBannerTitle")}
            </Typography>
            <Typography
              component="div"
              variant="body2"
              sx={{ opacity: 0.95, mt: 0.5 }}
            >
              {showIosInstallHint
                ? t("pwa.installBannerIosBody")
                : t("pwa.installBannerBody")}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      <Snackbar
        open={showPushBanner}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          display: { xs: "flex", md: "none" },
          bottom: { xs: 16, sm: 24 },
          px: 2,
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 520,
            alignItems: "flex-start",
            boxShadow: 6,
            "& .MuiAlert-message": { width: "100%" },
          }}
          action={
            <Box display="flex" gap={1} alignItems="center" mt={0.5}>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => dismissPushBanner()}
                disabled={pushSubscribeLoading}
                sx={{
                  borderColor: "rgba(255,255,255,0.45)",
                  color: "inherit",
                }}
              >
                {t("pwa.later")}
              </Button>
              <Button
                color="inherit"
                size="small"
                variant="contained"
                onClick={handleEnablePushNotifications}
                disabled={pushSubscribeLoading}
                sx={{
                  backgroundColor: "#fff",
                  color: "info.main",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                }}
              >
                {pushSubscribeLoading
                  ? t("pwa.enablingNotifications")
                  : t("pwa.enableNotifications")}
              </Button>
            </Box>
          }
        >
          <Box>
            <Typography component="div" fontWeight={700}>
              {t("pwa.notificationsBannerTitle")}
            </Typography>
            <Typography
              component="div"
              variant="body2"
              sx={{ opacity: 0.95, mt: 0.5 }}
            >
              {t("pwa.notificationsBannerBody")}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};

export default App;
